import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useLanguage } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import ArticleCard from "@/components/ArticleCard";
import { api } from "@/lib/api";

export default function Articoli() {
  const { t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/articles")
      .then((res) => setArticles(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => [...new Set(articles.map((a) => a.category))],
    [articles]
  );

  const filtered = useMemo(
    () =>
      articles.filter((a) => {
        const matchCat = !category || a.category === category;
        const q = query.trim().toLowerCase();
        const matchQuery =
          !q ||
          a.title.toLowerCase().includes(q) ||
          (a.title_en || "").toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q);
        return matchCat && matchQuery;
      }),
    [articles, category, query]
  );

  return (
    <div data-testid="articoli-page" className="pt-24 sm:pt-32 min-h-screen">
      <section className="container-editorial pb-10">
        <Reveal>
          <p className="eyebrow">{t.articles.eyebrow}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] text-slate-900 tracking-tight">
            {t.articles.title}
          </h1>
          <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-slate-600">{t.articles.sub}</p>
        </Reveal>
      </section>

      <section className="container-editorial pb-20 sm:pb-28">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between border-b border-slate-200 pb-6">
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="articles-filter-all"
              onClick={() => setCategory(null)}
              className={`rounded-full px-4 py-2 text-xs font-medium tracking-wide transition-colors duration-300 border ${
                category === null
                  ? "bg-brand text-white border-brand"
                  : "border-slate-200 text-slate-600 hover:border-brand hover:text-brand"
              }`}
            >
              Tutti / All
            </button>
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`articles-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(category === c ? null : c)}
                className={`rounded-full px-4 py-2 text-xs font-medium tracking-wide transition-colors duration-300 border ${
                  category === c
                    ? "bg-brand text-white border-brand"
                    : "border-slate-200 text-slate-600 hover:border-brand hover:text-brand"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="articles-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca / Search…"
              className="w-full rounded-full border border-slate-200 bg-white py-2 pl-9 pr-4 text-sm focus:outline-none focus:border-brand"
            />
          </div>
        </div>

        <div className="mt-10">
          {loading && <p className="text-sm font-light text-slate-500">{t.articles.loading}</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-sm font-light text-slate-500">{t.articles.empty}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filtered.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.08} className="h-full">
                <ArticleCard article={a} testid={`article-card-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
